import { useCallback, useContext, useEffect, useRef, useState } from 'react'

import { useRouter } from 'next/router'
import { useLocale, useTranslations } from 'next-intl'

import classNames from 'classnames'

import Container from '~/components/atoms/Container/Container'
import { Icon } from '~/components/atoms/Icon/Icon'
import type { ImageProps } from '~/components/atoms/Image/Image'
import ProfileImage, {
  ProfileImageType,
} from '~/components/atoms/ProfileImage/ProfileImage'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import TradingLogo from '~/components/atoms/TradingLogo/TradingLogo'
import { GlobalStoreContext } from '~/contexts/GlobalStoreContext'
import { useInPageNavContext } from '~/contexts/InPageNavContext'
import { useMediaQuery } from '~/services/useMediaQuery'
import analyticsPusher, { analyticsEventTypes } from '~/utils/analyticsPusher'
import { getLocalizedHardCodedSlug } from '~/utils/getLocalizedHardCodedSlug'
import { useScrollPosition } from '~/utils/useScrollPosition'

import styles from './StickySectionHeader.module.css'

const buyregex = /buyer'?s guide/i
const sellregex = /seller'?s guide/i
const valuationregex = /valuation guide/i

export interface StickySectionHeaderProps {
  type?: string
  id?: string
  image?: ImageProps['asset']
  totalWorks?: number
  slug: string
  name: string
  testID?: string
  category?: string
  title?: string
  stickyOverrideCta?: string
  noMarginBottom?: boolean
  primaryCta?: any
  secondaryCta?: any
  renderCtas?: boolean
  customTaxonomy?: string
}

export interface StickySectionHeaderCompProps extends StickySectionHeaderProps {
  parentPosition: number
  parentHeight: number
}

export default function StickySectionHeader({
  type = 'page',
  totalWorks,
  slug,
  name,
  id,
  image,
  testID,
  parentPosition,
  parentHeight,
  category,
  title,
  stickyOverrideCta,
  noMarginBottom = false,
  primaryCta,
  secondaryCta,
  renderCtas = false,
}: StickySectionHeaderCompProps) {
  const t = useTranslations('common')
  const isMobile = useMediaQuery(`(max-width: 1499px)`)
  const isSmallerNav = useMediaQuery(`(max-width: 1200px)`)
  const [isNotSticky, setIsNotSticky] = useState(true)
  const { mainHeaderVisible } = useInPageNavContext()
  const { stateDispatch } = useContext(GlobalStoreContext)
  const router = useRouter()

  const ref = useRef<any>(null)

  const [initialFilterPosition, setInitialFilterPosition] = useState<
    number | null
  >(null)

  useEffect(() => {
    const scrollTop = window.scrollY || document.documentElement.scrollTop
    // Need the position to equate to around:
    // 1220 - tablet
    // 1077 - for mobile

    setInitialFilterPosition(parentPosition + scrollTop + parentHeight)
  }, [parentPosition])

  useScrollPosition(
    ({ currPos }) => {
      let position = initialFilterPosition
      if (mainHeaderVisible)
        position = Number(initialFilterPosition) - (isSmallerNav ? 66 : 88)

      // setIsNotSticky(currPos.y > Number(`-${position}`))
    },
    [initialFilterPosition, mainHeaderVisible, parentPosition]
  )
  const locale = useLocale()

  const works = `${totalWorks} ${t('works')}`
  const isGenreOrArtist = ['genre', 'artist'].includes(`${type}`)

  const onClick = useCallback((e: any, link: any) => {
    analyticsPusher(analyticsEventTypes.gaEvent, {
      gaEventName: 'Sticky Link Bar - Articles',
    })

    if (
      (category === 'guides' &&
        title &&
        (sellregex.test(title) || valuationregex.test(title))) ||
      stickyOverrideCta === 'get-a-valuation'
    ) {
      // sellers guide instantiate the valuationre
      e.preventDefault()
      router.push('/valuations')
      // return stateDispatch({
      //   type: GlobalStoreProps.UPDATE_MODAL_STATE,
      //   payload: { isOpen: true, content: FormTypes.valuation },
      // })
      return
    }

    e.preventDefault()
    router.push(link)
  }, [])

  const renderGenreArtist = () => {
    return (
      <>
        <Text Tag="p" size={TextSizes.primaryTitleSml}>
          {name}
        </Text>

        <Text Tag="p" italic size={TextSizes.magSecondaryTitleXSml}>
          {works}
        </Text>
      </>
    )
  }

  const getCtaType = (type: string) => {
    const tradingFloor = {
      component: <TradingLogo textToPrepend={'Live '} />,
      link: getLocalizedHardCodedSlug('trading-floor', locale),
    }

    switch (type) {
      case 'genre-artist':
        return {
          component: renderGenreArtist(),
          link:
            locale !== 'en'
              ? `/${locale}/artist-${slug.replace('/', '')}`
              : `/artist-${slug.replace('/', '')}`,
        }
      case 'market-reports':
        if (locale !== 'en') {
          return {
            component: null,
            link: null,
          }
        }
        return {
          component: (
            <Text
              size={TextSizes.primaryTitleSml}
              className={styles.tradingBrand}
              Tag={'p'}
            >
              Market Reports
            </Text>
          ),
          link: '/market-reports',
        }
      case 'myportfolio':
        return {
          component: (
            <Text
              size={TextSizes.primaryTitleSml}
              className={classNames(styles.tradingBrand, styles.ctaButtonText)}
              Tag={'p'}
            >
              <em>My</em>Portfolio
            </Text>
          ),
          link: getLocalizedHardCodedSlug('my-portfolio', locale),
        }
      case 'get-a-valuation': {
        return {
          component: (
            <Text
              size={TextSizes.primaryTitleSml}
              className={classNames(styles.tradingBrand, styles.ctaButtonText)}
              Tag={'p'}
            >
              Get a valuation
            </Text>
          ),
        }
      }
      case 'trading-flooor': {
        return tradingFloor
      }
      default:
        return tradingFloor
    }
  }

  const getSecondaryCta = () => {
    const check = stickyOverrideCta || category

    if (isGenreOrArtist && !stickyOverrideCta) return getCtaType('genre-artist')

    if (check === 'collecting' || check === 'myportfolio')
      return getCtaType('trading-floor')

    return getCtaType('market-reports')
  }

  const component = getSecondaryCta() as any
  if (!component.component) {
    return null
  }

  return (
    <div
      className={classNames(styles.base, {
        [styles.stickyBase]: !isNotSticky && isMobile,
        [styles.mainHeaderVisible]:
          !isNotSticky && mainHeaderVisible && isMobile,
        [styles.noMarginBottom]: noMarginBottom,
        [styles.hideSubNav]:
          noMarginBottom && !isNotSticky && mainHeaderVisible && isMobile,
      })}
      ref={ref}
    >
      <Container>
        <div
          className={classNames(styles.sticky, {
            [styles.stuckToTop]: !isNotSticky && isMobile && !noMarginBottom,
            [styles.mergeNav]: !isNotSticky && mainHeaderVisible && isMobile,
          })}
          data-testid={testID}
        >
          <div className={styles.outer}>
            {renderCtas && (primaryCta || secondaryCta) ? (
              <div className={styles.ctasContainer}>
                {primaryCta && (
                  <div className={styles.ctaButton}>{primaryCta}</div>
                )}
                {secondaryCta && (
                  <div className={styles.ctaButton}>{secondaryCta}</div>
                )}
              </div>
            ) : null}
            <div
              className={classNames([
                styles.inner,
                styles.contain,
                {
                  [styles.containMobile]: isMobile,
                  [styles.justifyEnd]:
                    renderCtas && (primaryCta || secondaryCta),
                },
              ])}
            >
              <div
                className={classNames(
                  styles.wrapper,
                  !isNotSticky && styles.stickyWrapper,
                  styles.valuationBtn,
                  {
                    [styles.containWidth]:
                      renderCtas && (primaryCta || secondaryCta),
                  }
                )}
                onClick={e => onClick(e, component?.link as any)}
              >
                <div className={styles.content}>
                  {isGenreOrArtist && !stickyOverrideCta && image && id && (
                    <div className={styles.profileImage}>
                      <ProfileImage
                        name={name}
                        image={image}
                        showName={false}
                        _id={id}
                        inline={true}
                        smallImage={true}
                        type={ProfileImageType.Artist}
                      />
                    </div>
                  )}
                  <div className={styles.prose}>
                    <div className={styles.tradingLogo}>
                      {component.component}
                    </div>
                  </div>
                </div>
                <div className={styles.arrowLink}>
                  <Icon type="Arrow" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </div>
  )
}
